import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { authAPI } from '../api/api';
import ProfileIconSelector from '../components/profile/ProfileIconSelector';
import { 
  UserIcon, 
  KeyIcon,
  EyeIcon,
  EyeSlashIcon,
  CheckIcon,
  ExclamationTriangleIcon,
  PencilIcon
} from '@heroicons/react/24/outline';

const ProfilePage = () => {
  const { user, changePassword, updateUser } = useAuth();
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordMessage, setPasswordMessage] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  
  // Profil güncelleme state'leri
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [profileData, setProfileData] = useState({
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    profile_icon: user?.profile_icon || 'icons8-user-50'
  });
  const [profileMessage, setProfileMessage] = useState('');
  const [profileSuccess, setProfileSuccess] = useState(false);
  const [isProfileLoading, setIsProfileLoading] = useState(false);

  // Kullanıcı adının ilk harfini al
  const getUserInitial = () => {
    if (user?.first_name) return user.first_name.charAt(0);
    if (user?.full_name) return user.full_name.charAt(0);
    if (user?.username) return user.username.charAt(0);
    return 'U';
  };

  // Tam adı al
  const getFullName = () => {
    if (user?.full_name) return user.full_name;
    if (user?.first_name && user?.last_name) {
      return `${user.first_name} ${user.last_name}`;
    }
    if (user?.username) return user.username;
    return 'Unknown User';
  };

  // Profil icon'unu al
  const getProfileIcon = () => {
    if (user?.profile_icon && user.profile_icon.startsWith('icons8-')) {
      return (
        <img
          src={`/profile-icons/${user.profile_icon}.png`}
          alt="Profile Icon"
          className="w-16 h-16 rounded-full object-contain bg-gray-100 dark:bg-gray-700"
          onError={(e) => {
            e.target.style.display = 'none';
            e.target.nextSibling.style.display = 'flex';
          }}
        />
      );
    }
    return (
      <div className="w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center text-white font-semibold text-xl">
        {getUserInitial()}
      </div>
    );
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleProfileChange = (field, value) => {
    setProfileData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setIsProfileLoading(true);
    setProfileMessage('');

    try {
      const response = await authAPI.updateProfile(profileData);
      
      if (response.success) {
        setProfileMessage('Profil başarıyla güncellendi!');
        setProfileSuccess(true);
        setIsEditingProfile(false);
        
        // AuthContext'teki user bilgisini güncelle
        if (updateUser) {
          updateUser(response.data.user);
        }
      } else {
        setProfileMessage(response.message || 'Profil güncellenemedi');
        setProfileSuccess(false);
      }
    } catch (error) {
      setProfileMessage('Profil güncellenirken bir hata oluştu');
      setProfileSuccess(false);
    } finally {
      setIsProfileLoading(false);
    }

    // Clear message after 3 seconds
    setTimeout(() => {
      setProfileMessage('');
      setProfileSuccess(false);
    }, 3000);
  };

  const handleEditProfile = () => {
    setIsEditingProfile(true);
    setProfileData({
      first_name: user?.first_name || '',
      last_name: user?.last_name || '',
      profile_icon: user?.profile_icon || 'icons8-user-50'
    });
  };

  const handleCancelEdit = () => {
    setIsEditingProfile(false);
    setProfileData({
      first_name: user?.first_name || '',
      last_name: user?.last_name || '',
      profile_icon: user?.profile_icon || 'icons8-user-50'
    });
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    
    // Basic validation
    if (!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
      setPasswordMessage('Tüm alanlar zorunludur');
      setPasswordSuccess(false);
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordMessage('Yeni şifreler eşleşmiyor');
      setPasswordSuccess(false);
      return;
    }

    if (passwordData.newPassword.length < 6) {
      setPasswordMessage('Yeni şifre en az 6 karakter olmalıdır');
      setPasswordSuccess(false);
      return;
    }

    setIsLoading(true);

    try {
      const result = await changePassword({
        current_password: passwordData.currentPassword,
        new_password: passwordData.newPassword
      });

      if (result.success) {
        setPasswordMessage('Şifre başarıyla değiştirildi!');
        setPasswordSuccess(true);
        setPasswordData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
      } else {
        setPasswordMessage(result.message || 'Şifre değiştirilemedi');
        setPasswordSuccess(false);
      }
    } catch (error) {
      setPasswordMessage('Şifre değiştirirken bir hata oluştu');
      setPasswordSuccess(false);
    } finally {
      setIsLoading(false);
    }

    // Clear message after 3 seconds
    setTimeout(() => {
      setPasswordMessage('');
      setPasswordSuccess(false);
    }, 3000);
  };

  return (
    <div className="flex-1 bg-gray-50 dark:bg-gray-900 p-6 overflow-auto">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8 pt-4">
          <p className="text-sm font-medium text-blue-600 dark:text-blue-400 mb-2">
            Hoşgeldiniz, {getFullName()}
          </p>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Profile</h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Manage your personal information and account settings
          </p>
        </div>

        <div className="space-y-6">
          {/* Profile Information */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
                <UserIcon className="w-5 h-5 mr-2" />
                Personal Information
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                Your account details and contact information
              </p>
            </div>
            <div className="p-6">
              <div className="flex items-center space-x-4">
                <div className="relative">
                  {getProfileIcon()}
                  <div className="w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center text-white font-semibold text-xl hidden">
                    {getUserInitial()}
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                    {getFullName()}
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {user?.email || 'Email not available'}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-500">
                    {user?.role_name || 'Role'} • {user?.department?.department_name || 'Department'}
                  </p>
                </div>
                <button 
                  onClick={handleEditProfile}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center space-x-2"
                >
                  <PencilIcon className="w-4 h-4" />
                  <span>Edit Profile</span>
                </button>
              </div>
              
              {/* Additional Info Grid */}
              <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Employee ID
                  </label>
                  <p className="text-sm text-gray-900 dark:text-white">{user?.id || 'N/A'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Department
                  </label>
                  <p className="text-sm text-gray-900 dark:text-white">{user?.department?.department_name || 'N/A'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Hotel
                  </label>
                  <p className="text-sm text-gray-900 dark:text-white">{user?.hotel?.hotel_name || 'N/A'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Role
                  </label>
                  <p className="text-sm text-gray-900 dark:text-white">{user?.role_name || 'N/A'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Account Status
                  </label>
                  <p className="text-sm text-gray-900 dark:text-white">{user?.is_active ? 'Active' : 'Inactive'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Member Since
                  </label>
                  <p className="text-sm text-gray-900 dark:text-white">
                    {user?.date_joined ? new Date(user.date_joined).toLocaleDateString('tr-TR') : 'N/A'}
                  </p>
                </div>
              </div>

              {/* Profile Edit Form */}
              {isEditingProfile && (
                <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                  <form onSubmit={handleProfileSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* First Name */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          First Name
                        </label>
                        <input
                          type="text"
                          value={profileData.first_name}
                          onChange={(e) => handleProfileChange('first_name', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          placeholder="Enter first name"
                          disabled={isProfileLoading}
                        />
                      </div>

                      {/* Last Name */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Last Name
                        </label>
                        <input
                          type="text"
                          value={profileData.last_name}
                          onChange={(e) => handleProfileChange('last_name', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          placeholder="Enter last name"
                          disabled={isProfileLoading}
                        />
                      </div>
                    </div>

                    {/* Profile Icon Selector */}
                    <div>
                      <ProfileIconSelector
                        selectedIcon={profileData.profile_icon}
                        onIconSelect={(iconId) => handleProfileChange('profile_icon', iconId)}
                      />
                    </div>

                    {/* Message */}
                    {profileMessage && (
                      <div className={`flex items-center space-x-2 p-3 rounded-lg ${
                        profileSuccess 
                          ? 'bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-400'
                          : 'bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-400'
                      }`}>
                        {profileSuccess ? (
                          <CheckIcon className="w-4 h-4" />
                        ) : (
                          <ExclamationTriangleIcon className="w-4 h-4" />
                        )}
                        <span className="text-sm">{profileMessage}</span>
                      </div>
                    )}

                    {/* Form Actions */}
                    <div className="flex items-center space-x-3 pt-4">
                      <button
                        type="submit"
                        disabled={isProfileLoading}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isProfileLoading ? 'Updating...' : 'Update Profile'}
                      </button>
                      <button
                        type="button"
                        onClick={handleCancelEdit}
                        disabled={isProfileLoading}
                        className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                </div>
              )}
            </div>
          </div>

          {/* Change Password */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
                <KeyIcon className="w-5 h-5 mr-2" />
                Change Password
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                Update your password to keep your account secure
              </p>
            </div>
            <div className="p-6">
              <form onSubmit={handlePasswordSubmit} className="space-y-4">
                {/* Current Password */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Current Password
                  </label>
                  <div className="relative">
                    <input
                      type={showCurrentPassword ? 'text' : 'password'}
                      name="currentPassword"
                      value={passwordData.currentPassword}
                      onChange={handlePasswordChange}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white pr-10"
                      placeholder="Enter current password"
                      disabled={isLoading}
                    />
                    <button
                      type="button"
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                    >
                      {showCurrentPassword ? <EyeSlashIcon className="w-4 h-4" /> : <EyeIcon className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                {/* New Password */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    New Password
                  </label>
                  <div className="relative">
                    <input
                      type={showNewPassword ? 'text' : 'password'}
                      name="newPassword"
                      value={passwordData.newPassword}
                      onChange={handlePasswordChange}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white pr-10"
                      placeholder="Enter new password"
                      disabled={isLoading}
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                    >
                      {showNewPassword ? <EyeSlashIcon className="w-4 h-4" /> : <EyeIcon className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                {/* Confirm Password */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Confirm New Password
                  </label>
                  <div className="relative">
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      name="confirmPassword"
                      value={passwordData.confirmPassword}
                      onChange={handlePasswordChange}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white pr-10"
                      placeholder="Confirm new password"
                      disabled={isLoading}
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                    >
                      {showConfirmPassword ? <EyeSlashIcon className="w-4 h-4" /> : <EyeIcon className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                {/* Message */}
                {passwordMessage && (
                  <div className={`flex items-center space-x-2 p-3 rounded-lg ${
                    passwordSuccess 
                      ? 'bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-400'
                      : 'bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-400'
                  }`}>
                    {passwordSuccess ? (
                      <CheckIcon className="w-4 h-4" />
                    ) : (
                      <ExclamationTriangleIcon className="w-4 h-4" />
                    )}
                    <span className="text-sm">{passwordMessage}</span>
                  </div>
                )}

                {/* Submit Button */}
                <div className="pt-4">
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isLoading ? 'Changing Password...' : 'Change Password'}
                  </button>
                </div>
              </form>
            </div>
          </div>

          {/* Account Information */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Account Information
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                Your account details and system information
              </p>
            </div>
            <div className="p-6">
              <div className="space-y-3">
                <div className="flex items-center justify-between py-2">
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">User ID</p>
                    <p className="text-xs text-gray-600 dark:text-gray-400">{user?.id || 'N/A'}</p>
                  </div>
                </div>
                <div className="flex items-center justify-between py-2">
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">Email</p>
                    <p className="text-xs text-gray-600 dark:text-gray-400">{user?.email || 'N/A'}</p>
                  </div>
                </div>
                <div className="flex items-center justify-between py-2">
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">Account Created</p>
                    <p className="text-xs text-gray-600 dark:text-gray-400">
                      {user?.date_joined ? new Date(user.date_joined).toLocaleDateString('tr-TR') : 'N/A'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;